import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  getCiclos, createCiclo, activarCiclo, 
  getCalendario, createCalendarioEntry, deleteCalendarioEntry,
  uploadLogo, getLogoInfo, getLogoUrl, downloadBackup, restoreBackup
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, CalendarBlank, GraduationCap, Check, X, Sun, Umbrella, Image, Upload, Download, ShieldCheck, Warning
} from '@phosphor-icons/react';
import { format, parseISO, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';

const TIPOS_DIA = [
  { value: 'clase', label: 'Día de Clase', color: 'bg-green-500' },
  { value: 'feriado', label: 'Día Feriado', color: 'bg-red-500' },
  { value: 'suspension', label: 'Suspensión', color: 'bg-amber-500' },
  { value: 'vacaciones', label: 'Vacaciones', color: 'bg-blue-500' }
];

const ConfiguracionPage = () => {
  const { user } = useAuth();
  const [ciclos, setCiclos] = useState([]);
  const [calendario, setCalendario] = useState([]);
  const [selectedCiclo, setSelectedCiclo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCicloModal, setShowCicloModal] = useState(false);
  const [showDiaModal, setShowDiaModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [backupFile, setBackupFile] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [activeTab, setActiveTab] = useState('ciclos');
  const [logoInfo, setLogoInfo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const [cicloForm, setCicloForm] = useState({
    nombre: '',
    fecha_inicio: '',
    fecha_fin: ''
  });

  const [diaForm, setDiaForm] = useState({
    tipo: '',
    descripcion: ''
  });

  const fetchCiclos = useCallback(async () => {
    try {
      const response = await getCiclos();
      setCiclos(response.data);
      const activo = response.data.find(c => c.activo);
      if (activo) setSelectedCiclo(activo);
    } catch (error) {
      toast.error('Error al cargar ciclos');
    }
  }, []);

  const fetchCalendario = useCallback(async () => {
    if (!selectedCiclo) return;
    try {
      const response = await getCalendario(selectedCiclo.id);
      setCalendario(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error fetching calendario:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedCiclo]);

  useEffect(() => {
    fetchCiclos();
  }, [fetchCiclos]);

  const fetchLogoInfo = useCallback(async () => {
    if (!selectedCiclo) return;
    try {
      const response = await getLogoInfo(selectedCiclo.id);
      setLogoInfo(response.data);
      if (response.data?.path) {
        setLogoPreview(getLogoUrl(selectedCiclo.id));
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error fetching logo info:', error);
      }
    }
  }, [selectedCiclo]);

  useEffect(() => {
    if (selectedCiclo) {
      fetchCalendario();
      fetchLogoInfo();
    }
  }, [selectedCiclo, fetchCalendario, fetchLogoInfo]);

  const handleUploadLogo = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedCiclo) return;
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('El archivo no debe superar 2MB');
      return;
    }
    
    try {
      await uploadLogo(file, selectedCiclo.id);
      toast.success('Logo subido correctamente');
      fetchLogoInfo();
    } catch (error) {
      toast.error('Error al subir el logo');
    }
    e.target.value = '';
  };

  const handleDownloadBackup = async () => {
    setBackupLoading(true);
    try {
      const resp = await downloadBackup();
      const blob = new Blob([resp.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `backup_secbj_${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Respaldo descargado correctamente. Guarda el archivo en un lugar seguro.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar el respaldo');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestoreBackup = async () => {
    if (!backupFile) return;
    const confirmed = window.confirm(
      '⚠️ ATENCIÓN: Restaurar un respaldo SOBRESCRIBE todos los datos actuales del sistema.\n\n' +
      'Esta acción no se puede deshacer. ¿Deseas continuar?'
    );
    if (!confirmed) return;
    setRestoreLoading(true);
    setRestoreResult(null);
    try {
      const resp = await restoreBackup(backupFile);
      setRestoreResult(resp.data.resumen || {});
      toast.success('Respaldo restaurado correctamente. Cierra sesión y vuelve a entrar para reflejar los cambios.');
      setBackupFile(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar el respaldo');
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleCreateCiclo = async (e) => {
    e.preventDefault();
    try {
      await createCiclo(cicloForm);
      toast.success('Ciclo escolar creado');
      setShowCicloModal(false);
      setCicloForm({ nombre: '', fecha_inicio: '', fecha_fin: '' });
      fetchCiclos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear ciclo');    }
  };

  const handleActivarCiclo = async (cicloId) => {
    try {
      await activarCiclo(cicloId);
      toast.success('Ciclo activado');
      fetchCiclos();
    } catch (error) {
      toast.error('Error al activar ciclo');
    }
  };

  const handleSaveDia = async () => {
    if (!selectedDate || !diaForm.tipo || !selectedCiclo) return;
    try {
      await createCalendarioEntry({
        ciclo_escolar_id: selectedCiclo.id,
        fecha: format(selectedDate, 'yyyy-MM-dd'),
        tipo: diaForm.tipo,
        descripcion: diaForm.descripcion
      });
      toast.success('Día guardado');
      setShowDiaModal(false);
      setDiaForm({ tipo: '', descripcion: '' });
      fetchCalendario();
    } catch (error) {
      toast.error('Error al guardar día');
    }
  };

  const handleDeleteDia = async (fecha) => {
    if (!selectedCiclo) return;
    try {
      await deleteCalendarioEntry(fecha, selectedCiclo.id);
      toast.success('Día eliminado');
      fetchCalendario();
    } catch (error) {
      toast.error('Error al eliminar día');
    }
  };

  const openDiaModal = (date) => {
    setSelectedDate(date);
    const existing = calendario.find(c => c.fecha === format(date, 'yyyy-MM-dd'));
    if (existing) {
      setDiaForm({ tipo: existing.tipo, descripcion: existing.descripcion || '' });
    } else {
      setDiaForm({ tipo: '', descripcion: '' });
    }
    setShowDiaModal(true);
  };

  const getDayModifiers = () => {
    const modifiers = {};
    TIPOS_DIA.forEach(tipo => {
      modifiers[tipo.value] = calendario
        .filter(c => c.tipo === tipo.value)
        .map(c => parseISO(c.fecha));
    });
    return modifiers;
  };

  const getDayStyles = () => {
    const styles = {};
    TIPOS_DIA.forEach(tipo => {
      styles[tipo.value] = { 
        backgroundColor: tipo.color.replace('bg-', '').includes('green') ? '#22c55e' :
                        tipo.color.replace('bg-', '').includes('red') ? '#ef4444' :
                        tipo.color.replace('bg-', '').includes('amber') ? '#f59e0b' : '#3b82f6',
        color: 'white',
        borderRadius: '50%'
      };
    });
    return styles;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="configuracion-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">Configuración</h1>
          <p className="text-muted-foreground">Administración del sistema escolar</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="ciclos" className="flex items-center gap-2">
            <GraduationCap size={16} />
            Ciclos Escolares
          </TabsTrigger>
          <TabsTrigger value="calendario" className="flex items-center gap-2">
            <CalendarBlank size={16} />
            Calendario Escolar
          </TabsTrigger>
          <TabsTrigger value="logo" className="flex items-center gap-2">
            <Image size={16} />
            Logo Institucional
          </TabsTrigger>
          {user?.rol === 'administrador' && (
            <TabsTrigger value="backup" className="flex items-center gap-2" data-testid="tab-backup">
              <ShieldCheck size={16} />
              Respaldo
            </TabsTrigger>
          )}
        </TabsList>

        {/* Ciclos Tab */}
        <TabsContent value="ciclos" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowCicloModal(true)} data-testid="add-ciclo">
              <Plus size={16} className="mr-2" />
              Nuevo Ciclo
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {ciclos.map((ciclo) => (
              <Card 
                key={ciclo.id} 
                className={`card-hover ${ciclo.activo ? 'border-primary border-2' : ''}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg font-heading">{ciclo.nombre}</CardTitle>
                      <CardDescription>
                        {ciclo.fecha_inicio} - {ciclo.fecha_fin}
                      </CardDescription>
                    </div>
                    {ciclo.activo && (
                      <Badge className="bg-green-100 text-green-800">Activo</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {!ciclo.activo && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                      onClick={() => handleActivarCiclo(ciclo.id)}
                    >
                      <Check size={16} className="mr-2" />
                      Activar
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Calendario Tab */}
        <TabsContent value="calendario" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <Label className="text-xs">Ciclo Escolar</Label>
              <Select 
                value={selectedCiclo?.id || ''} 
                onValueChange={(v) => setSelectedCiclo(ciclos.find(c => c.id === v))}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Seleccionar ciclo" />
                </SelectTrigger>
                <SelectContent>
                  {ciclos.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-2 items-center">
              {TIPOS_DIA.map(tipo => (
                <div key={tipo.value} className="flex items-center gap-1">
                  <div className={`w-3 h-3 rounded-full ${tipo.color}`} />
                  <span className="text-xs text-muted-foreground">{tipo.label}</span>
                </div>
              ))}
            </div>
          </div>

          {selectedCiclo && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Calendario</CardTitle>
                  <CardDescription>Haz clic en un día para configurarlo</CardDescription>
                </CardHeader>
                <CardContent className="flex justify-center">
                  <Calendar
                    mode="single"
                    onSelect={openDiaModal}
                    locale={es}
                    modifiers={getDayModifiers()}
                    modifiersStyles={getDayStyles()}
                    className="rounded-md border"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Días Configurados</CardTitle>
                  <CardDescription>Lista de días especiales</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {calendario.filter(c => c.tipo !== 'clase').length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No hay días especiales configurados
                      </p>
                    ) : (
                      calendario.filter(c => c.tipo !== 'clase').map((dia) => (
                        <div 
                          key={dia.fecha} 
                          className="flex items-center justify-between p-3 rounded-lg border"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${TIPOS_DIA.find(t => t.value === dia.tipo)?.color}`} />
                            <div>
                              <p className="font-medium text-sm">
                                {format(parseISO(dia.fecha), "d 'de' MMMM, yyyy", { locale: es })}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {TIPOS_DIA.find(t => t.value === dia.tipo)?.label}
                                {dia.descripcion && ` - ${dia.descripcion}`}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            onClick={() => handleDeleteDia(dia.fecha)}
                          >
                            <X size={16} />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Logo Tab */}
        <TabsContent value="logo" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image size={20} />
                Logo Institucional
              </CardTitle>
              <CardDescription>
                Sube el logo institucional que se usará en reportes y credenciales. 
                Puedes configurar un logo diferente para cada ciclo escolar.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Selector de ciclo */}
              <div className="flex items-center gap-4">
                <Label>Ciclo Escolar:</Label>
                <Select 
                  value={selectedCiclo?.id || ''} 
                  onValueChange={(v) => setSelectedCiclo(ciclos.find(c => c.id === v))}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Seleccionar ciclo" />
                  </SelectTrigger>
                  <SelectContent>
                    {ciclos.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.nombre} {c.activo && '(Activo)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Preview del logo */}
              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="flex-1">
                  <p className="text-sm font-medium mb-3">Logo Actual</p>
                  <div className="border-2 border-dashed rounded-lg p-8 text-center bg-muted/30">
                    {logoInfo?.path ? (
                      <img 
                        src={logoPreview} 
                        alt="Logo institucional" 
                        className="max-w-[200px] max-h-[200px] mx-auto object-contain"
                        onError={() => setLogoPreview(null)}
                      />
                    ) : (
                      <div className="text-muted-foreground">
                        <Image size={64} className="mx-auto mb-2 opacity-30" />
                        <p>No hay logo configurado para este ciclo</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex-1">
                  <p className="text-sm font-medium mb-3">Subir Nuevo Logo</p>
                  <div className="border-2 border-dashed rounded-lg p-8 text-center">
                    <Upload size={48} className="mx-auto mb-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground mb-4">
                      Formatos permitidos: JPG, PNG, WEBP<br />
                      Tamaño máximo: 2MB<br />
                      Recomendado: 500x500 px
                    </p>
                    <label className="cursor-pointer">
                      <Button type="button" variant="outline" asChild>
                        <span>
                          <Upload size={16} className="mr-2" />
                          Seleccionar Archivo
                        </span>
                      </Button>
                      <input 
                        type="file" 
                        className="hidden" 
                        accept="image/jpeg,image/png,image/webp" 
                        onChange={handleUploadLogo}
                        data-testid="upload-logo-input"
                      />
                    </label>
                  </div>
                </div>
              </div>

              {/* Información adicional */}
              <Card className="bg-muted/30">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">
                    <strong>Uso del logo:</strong> El logo se incluirá automáticamente en:
                  </p>
                  <ul className="text-sm text-muted-foreground mt-2 list-disc list-inside">
                    <li>Reportes de asistencia (PDF)</li>
                    <li>Reportes de expediente (PDF)</li>
                    <li>Reportes de inscripciones (PDF)</li>
                    <li>Credenciales estudiantiles</li>
                  </ul>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Backup Tab (Solo Administrador) */}
        {user?.rol === 'administrador' && (
          <TabsContent value="backup" className="mt-4 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <ShieldCheck size={20} />
                  Respaldo del sistema
                </CardTitle>
                <CardDescription>
                  Descarga o restaura una copia completa de todos los datos del sistema. Cada operación queda registrada en la bitácora y el registro de auditoría.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="rounded-md border p-4 space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h4 className="font-medium">Descargar respaldo</h4>
                      <p className="text-sm text-muted-foreground">Genera un archivo JSON con alumnos, inscripciones, usuarios, incidencias, bitácora, auditoría, calendario, ciclos y configuración.</p>
                    </div>
                    <Button onClick={handleDownloadBackup} disabled={backupLoading} data-testid="download-backup">
                      <Download size={16} className="mr-1" />
                      {backupLoading ? 'Generando…' : 'Descargar respaldo'}
                    </Button>
                  </div>
                </div>

                <div className="rounded-md border p-4 space-y-3">
                  <div>
                    <h4 className="font-medium flex items-center gap-2 text-destructive">
                      <Warning size={16} weight="fill" />
                      Cargar respaldo
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Restaura un archivo de respaldo previamente descargado. <strong>Sobrescribe todos los datos actuales.</strong> Esta acción no se puede deshacer.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Input
                      type="file"
                      accept="application/json,.json"
                      onChange={(e) => setBackupFile(e.target.files?.[0] || null)}
                      data-testid="upload-backup-file"
                    />
                    <Button
                      variant="destructive"
                      onClick={handleRestoreBackup}
                      disabled={!backupFile || restoreLoading}
                      data-testid="restore-backup"
                    >
                      <Upload size={16} className="mr-1" />
                      {restoreLoading ? 'Restaurando…' : 'Restaurar respaldo'}
                    </Button>
                  </div>
                  {backupFile && (
                    <p className="text-xs text-muted-foreground">
                      Archivo seleccionado: <strong className="break-all">{backupFile.name}</strong>
                    </p>
                  )}
                  {restoreResult && (
                    <div className="mt-2 rounded-md bg-muted/50 p-3 text-xs">
                      <p className="font-medium">Resumen de la restauración:</p>
                      <ul className="mt-1 space-y-0.5">
                        {Object.entries(restoreResult.restauradas || {}).map(([coll, count]) => (
                          <li key={coll}>• {coll}: {count} registro(s)</li>
                        ))}
                        {(restoreResult.errores || []).length > 0 && (
                          <li className="text-destructive">• Errores: {restoreResult.errores.length}</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Ciclo Modal */}
      <Dialog open={showCicloModal} onOpenChange={setShowCicloModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Nuevo Ciclo Escolar</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCiclo} className="space-y-4">
            <div>
              <Label>Nombre del Ciclo *</Label>
              <Input
                value={cicloForm.nombre}
                onChange={(e) => setCicloForm(p => ({ ...p, nombre: e.target.value }))}
                placeholder="Ej: 2024-2025"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha de Inicio *</Label>
                <Input
                  type="date"
                  value={cicloForm.fecha_inicio}
                  onChange={(e) => setCicloForm(p => ({ ...p, fecha_inicio: e.target.value }))}
                  required
                />
              </div>
              <div>
                <Label>Fecha de Fin *</Label>
                <Input
                  type="date"
                  value={cicloForm.fecha_fin}
                  onChange={(e) => setCicloForm(p => ({ ...p, fecha_fin: e.target.value }))}
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCicloModal(false)}>
                Cancelar
              </Button>
              <Button type="submit">Crear Ciclo</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Día Modal */}
      <Dialog open={showDiaModal} onOpenChange={setShowDiaModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              Configurar Día - {selectedDate && format(selectedDate, "d 'de' MMMM, yyyy", { locale: es })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Tipo de Día</Label>
              <Select value={diaForm.tipo} onValueChange={(v) => setDiaForm(p => ({ ...p, tipo: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_DIA.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Descripción (opcional)</Label>
              <Input
                value={diaForm.descripcion}
                onChange={(e) => setDiaForm(p => ({ ...p, descripcion: e.target.value }))}
                placeholder="Ej: Día de la Independencia"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiaModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveDia} disabled={!diaForm.tipo}>
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfiguracionPage;
